import { Component, ElementRef, EventEmitter, HostListener, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AgendaService } from 'src/app/services/agenda/agenda.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { PatientService } from 'src/app/services/patient/patient.service';
import { Intervenant } from 'src/app/utils/interfaces/new/intervenant';
import { EventResource, SuperTypeEnum } from 'src/app/utils/interfaces/new/event-resource.interface';
import { ReunionPluriprofessionnellePersonnelSoignant, TypeEnum as CaregiverTypeEnum } from 'src/app/utils/interfaces/new/ReunionPluriprofessionnellePersonnelSoignant';
import { environment } from 'src/environments/environment';

const TABS_MATCHING = {
  'internal-tab': 'tab-1',
  'linked-patient-tab': 'tab-2',
  'external-tab': 'tab-3',
};

interface CaregiverSelect {
  id: string;
  type: CaregiverTypeEnum;
}

@Component({
  selector: 'app-modal-select-caregivers',
  templateUrl: './modal-select-caregivers.component.html',
  styleUrls: [
    './modal-select-caregivers.component.scss',
    '../../fragments/modal/modal.fragment.scss'
  ]
})
export class ModalSelectCaregiversComponent implements OnInit {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  public patientsId: Array<string> = [];
  private _resources: Array<EventResource> = [];
  public resourcesStr: Array<string> = [];
  private _resourcesLinkedToPatients: Array<Intervenant> = [];
  public resourcesLinkedToPatientsStr: Array<string> = [];
  public selectedCaregivers: Array<CaregiverSelect> = [];
  public formGroupInternal: FormGroup;
  public formGroupLinkedPatients: FormGroup;
  public formGroupExternal: FormGroup;

  public closeEmitter = new EventEmitter();
  public currentFolderId = 'internal-tab';

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
      this.closeModal();
    }
  }

  constructor(
    private _renderer: Renderer2,
    private _modalSrv: ModalService,
    private _vcr: ViewContainerRef,
    private _agendaSrv: AgendaService,
    private _formBuilder: FormBuilder,
    private _patientSrv: PatientService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.initData();
  }

  public doSave(): void {
    const caregivers: Array<ReunionPluriprofessionnellePersonnelSoignant> = [];
    for (const scg of this.selectedCaregivers) {
      if (scg.type === CaregiverTypeEnum.RessourceInterne) {
        const caregiver = this._resources.find((r) => r.id === scg.id && scg.type === CaregiverTypeEnum.RessourceInterne);
        if (caregiver && !caregivers.find((cg) => cg.id === caregiver.id)) {
          caregivers.push({
            id: caregiver.id,
            type: CaregiverTypeEnum.RessourceInterne,
            nom: caregiver.nom
          });
        }
      } else if (scg.type === CaregiverTypeEnum.IntervenantLieAuPatient) {
        const caregiver = this._resourcesLinkedToPatients.find((r) => r.id === scg.id
          && scg.type === CaregiverTypeEnum.IntervenantLieAuPatient);
        if (caregiver && !caregivers.find((cg) => cg.id === caregiver.id)) {
          caregivers.push({
            id: caregiver.id,
            type: CaregiverTypeEnum.IntervenantLieAuPatient,
            nom: caregiver.libelle
          });
        }
      }
    }

    if (caregivers.length === 0 && this.formGroupExternal.get('name').value && this.formGroupExternal.get('name').value !== '') {
      caregivers.push({
        id: null,
        nom: this.formGroupExternal.get('name').value,
        type: CaregiverTypeEnum.IntervenantExterne
      });
    }
    this.closeModal(caregivers.length > 0 ? caregivers : undefined);
  }

  public doCancel(): void {
    this.closeModal();
  }

  public modelChanged(event: Array<string>): void {
    this.selectedCaregivers = [];
    for (const e of event) {
      const caregiver = this._resources.find(r => r.nom === e);
      if (caregiver) {
        this.selectedCaregivers.push({
          id: caregiver.id,
          type: CaregiverTypeEnum.RessourceInterne
        });
      }
    }
  }

  public modelChangedLinkedToPatients(event: Array<string>): void {
    this.selectedCaregivers = [];
    for (const e of event) {
      const caregiver = this._resourcesLinkedToPatients.find(r => r.libelle === e);
      if (caregiver) {
        this.selectedCaregivers.push({
          id: caregiver.id,
          type: CaregiverTypeEnum.IntervenantLieAuPatient
        });
      }
    }
  }

  public showTab(event: any): void {
    this.selectedCaregivers = [];
    const elemId = (event.srcElement).getAttribute('id');
    const tabsContent = document.getElementById('tabs-content');

    if (elemId === this.currentFolderId) {
      return;
    }

    (event.srcElement as Node).parentNode.childNodes.forEach((n) => {
      this._renderer.removeClass(n, 'active');
    });

    tabsContent.childNodes.forEach((n) => {
      this._renderer.removeClass(n, 'shown-tab');
    });

    this._renderer.addClass(document.getElementById(elemId), 'active');
    this._renderer.addClass(document.getElementById(TABS_MATCHING[elemId]), 'shown-tab');
    this.currentFolderId = elemId;
  }

  public closeModal(caregivers?: Array<ReunionPluriprofessionnellePersonnelSoignant>): void {
    this.closeEmitter.emit(caregivers);
  }

  private async initData(): Promise<void> {
    const loading = this._modalSrv.openLoading(this._vcr);
    try {
      this._resources = await this._getResources();
      for (const r of this._resources) {
        if (r.superType === SuperTypeEnum.Profil) {
          this.resourcesStr.push(r.nom);
        }
      }
      this.formGroupInternal = this._formBuilder.group({
        name: []
      });

      if (this.patientsId.length > 0) {
        this._resourcesLinkedToPatients = await this._getResourcesLinkedToPatients(this.patientsId);
        for (const r of this._resourcesLinkedToPatients) {
          this.resourcesLinkedToPatientsStr.push(r.libelle);
        }
      }
      this.formGroupLinkedPatients = this._formBuilder.group({
        name: []
      });

      this.formGroupExternal = this._formBuilder.group({
        name: []
      });
    } finally {
      this._modalSrv.close(loading);
    }
  }

  private async _getResources(): Promise<Array<EventResource>> {
    return new Promise<Array<EventResource>>(async (resolve, reject) => {
      try {
        const resources = await this._agendaSrv.getResources();
        resolve(resources);
      } catch (e) {
        reject(e);
      }
    });
  }

  private async _getResourcesLinkedToPatients(patientsId: Array<string>): Promise<Array<Intervenant>> {
    return new Promise<Array<Intervenant>>(async (resolve, reject) => {
      try {
        const resources = [];
        for (const p of patientsId) {
          const cor = await this._patientSrv.findOnePatientRepertoire(p);
          if (cor.intervenants && cor.intervenants.length > 0) {
            for (const c of cor.intervenants) {
              resources.push(c);
            }
          }
        }
        resolve(resources);
      } catch (e) {
        reject(e);
      }
    });
  }

}
